import * as _ from 'lodash';
import qs from 'qs';
import { Component, useCallback } from 'react';
import { Alert } from 'react-bootstrap';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import type { FunctionComponent, MouseEvent } from 'react';
import type { AnyAction } from 'redux';

import { BINARY_DECOMPOSITION } from '../../../shared/featureFlags/flagNames';
import { PREMIUM_COMP_SEC_PLAN_NAME } from '../../../shared/subscriptions';
import { window } from '../../globals';
import { useIsFeatureEnabled } from '../../helpers/features';
import { userHasPermissionToCreateOrgReports, userHasPermissionToCreateTeamReports } from '../../helpers/permissions';
import { useAsyncValue } from '../../hooks';
import { get } from '../../redux/api';
import OrganizationsModule from '../../redux/organizations';
import TeamsModule from '../../redux/teams';
import VulnsModule from '../../redux/vulns';
import utils, { hasError } from '../../utils';
import { ErrorAlert } from '../generic';
import Button from '../generic/Button';
import DropdownButton from '../generic/DropdownButton';
import { icons, IconText } from '../generic/Icon';
import Text from '../generic/Text';
import { useAddJobToNotificationQueue } from '../JobNotificationHub';
import ModalBulkReport from '../modals/ModalBulkReport';
import { useCanDisplaySecurityOptions } from '../projects/generic/hooks';
import { auditDueDiligenceReportFeature, issueDashboardFeature } from '../settings/billing/features/consts';
import SubscriptionBannerReportPage from '../settings/subscription/banners/SubscriptionBannerReportPage';
import type { IAsyncStatus, Task, TeamSelection } from '../../types';
import type { MessageOptions } from '../settings/billing/features/consts';

import DashboardReportCard from './DashboardReportCard';

type CheckAccessParams = {
  event?: MouseEvent<HTMLElement>;
  rejectMessageOptions?: MessageOptions;
};

type Props = {
  createAuditReport(teamIds?: number[] | 'null'): Promise<void>;
  createSecurityExport(): Promise<void>;
  onCreateIssueCSVExportClick: (teamIds?: TeamSelection) => Promise<void>;
  selectedTeams: TeamSelection;
  organizationsStatus: IAsyncStatus;
  creatingAuditReportLoading: boolean;
  creatingSecurityReportLoading: boolean;
  isIssueCSVExportLoading: boolean;
  isSecurityAndPDFReportsEnabled: boolean;
  isBinaryDecompositionEnabled: boolean;
  onAddJobToNotificationQueue: (jobToken: string, jobName: string) => void;
};

type DashboardActionCardsState = {
  showingModalBulkReport: boolean;
  showBinaryScansOption: boolean;
  successAlert: string | null;
};

type RenderBaseExportOptions = {
  title: string;
  description: string;
  onClick: (...arg: any[]) => Promise<void> | void;
  propsLoadingField: boolean;
  emailDeliveryMessage?: string;
};

const SUCCESS_MESSAGE_TIMEOUT = 5000;

const getGlobalIssuesCSVMessage = (method?: 'email' | 'download') => {
  if (method === 'email') {
    return 'Your report is being generated and will be emailed to you';
  }

  return 'Your report will begin downloading shortly.';
};

const GenerateReportIconText = (
  <IconText icon={icons.addToProject} text="Generate report" />
);

class DashboardActionCards extends Component<Props, DashboardActionCardsState> {
  constructor(props) {
    super(props);
    this.state = {
      showingModalBulkReport: false,
      successAlert: null,
    };
  }

  hideMessage = () => this.setState({ successAlert: null });

  checkAccess = ({ event, rejectMessageOptions }: CheckAccessParams): boolean => {
    // TODO: utils.verify_subscription should be able to differentiate between security or no security premium plans.
    // It's fine right now because we hide the security reports unless the org has security.
    if (!utils.verify_subscription('premium')) {
      window.showPlanSelectorModal(rejectMessageOptions);

      if (event) {
        event.preventDefault();
      }
      return false;
    }

    return true;
  };

  generateReport = async (params: {
    ev?: MouseEvent<HTMLElement>;
    submitFn: (...args: any[]) => Promise<AnyAction | void>;
    successAlert?: string | null;
    rejectMessageOptions?: MessageOptions;
    args?: unknown[];
  }) => {
    if (!this.checkAccess({ event: params?.ev, rejectMessageOptions: params.rejectMessageOptions })) {
      return;
    }

    try {
      this.setState({ successAlert: null });
      //  Returns the "fulfilled" or "rejected" Redux action.
      const action = await params.submitFn(...(params.args || []));
      const task = (action?.value as Response & { data?: Task })?.data ?? null;
      //  If there is a task, we can add it to the notification queue,
      //  which will poll the task status and notify the user when it's done.
      if (task?.jobToken) {
        this.props.onAddJobToNotificationQueue(task.jobToken, task.task);
      }

      this.setState({
        successAlert: params.successAlert ?? null,
      });
      setTimeout(() => this.hideMessage, SUCCESS_MESSAGE_TIMEOUT);
    } catch (e) {
      // Error is handled by Redux.
    }
  };

  generateAudit = async () => {
    const args = [_.isEmpty(this.props.selectedTeams) ? undefined : this.props.selectedTeams];

    await this.generateReport({
      submitFn: this.props.createAuditReport,
      successAlert: 'Your report is being generated and will be emailed to you.',
      rejectMessageOptions: {
        focusFeature: auditDueDiligenceReportFeature,
      },
      args,
    });
  };

  generateCSVExport = async (method?: 'email' | 'download') => {
    await this.generateReport({
      submitFn: async () => {
        const teamIds = this.props.selectedTeams;
        if (method === 'email') {
          return this.props.onCreateIssueCSVExportClick(teamIds);
        }
        //  Handle CSV download
        const params = _.isEmpty(this.props.selectedTeams)
          ? ''
          : `?${qs.stringify({ teamIds })}`;
        utils.openInNewTab(`/api/v2/issues/csv/global${params}`);
        return null;
      },
      rejectMessageOptions: {
        focusFeature: issueDashboardFeature,
      },
      successAlert: getGlobalIssuesCSVMessage(method),
    });
  };

  generateSecurityExport = async () => {
    await this.generateReport({
      submitFn: this.props.createSecurityExport,
      rejectMessageOptions: {
        tier: PREMIUM_COMP_SEC_PLAN_NAME,
      },
      successAlert: 'Your report is being generated and will be emailed to you.',
    });
  };

  generateReportBundle = () => {
    const hasAccess = this.checkAccess({
      rejectMessageOptions: {
      },
    });

    if (!hasAccess) {
      return;
    }
    this.setState({ showingModalBulkReport: true });
  };

  renderAuditReportGlobalTeamFilter = () => (
    <div>
      <DashboardReportCard
        title="Audit/Due Diligence Report"
        description="An organization-wide report on Issues and project changes."
      >
        <DropdownButton
          id="AuditReportOptions"
          title={GenerateReportIconText}
          size="large"
          onSelect={this.generateAudit}
          disabled={this.props.creatingAuditReportLoading}
        >
          <DropdownButton.Item eventKey="email">Email</DropdownButton.Item>
        </DropdownButton>
      </DashboardReportCard>
    </div>
  );

  renderBaseExport = (options: RenderBaseExportOptions) => {
    return (
      <DashboardReportCard
        title={options.title}
        description={options.description}
        emailDeliveryMessage={options.emailDeliveryMessage}
      >
        <DropdownButton
          id={`${options.title.replace(/\s+/g, '')}Options`}
          title={GenerateReportIconText}
          size="large"
          onSelect={options.onClick}
          disabled={options.propsLoadingField}
        >
          <DropdownButton.Item eventKey="email">Email</DropdownButton.Item>
        </DropdownButton>
      </DashboardReportCard>
    );
  };

  render() {
    // Report creation is limited to Org level users or Team level users with edit permissions.
    const canCreateReport = userHasPermissionToCreateOrgReports() || userHasPermissionToCreateTeamReports();
    return (
      <div className="column dashboard-actions">
        {hasError(this.props.organizationsStatus) && <ErrorAlert status={this.props.organizationsStatus} />}
        {this.state.successAlert && <Alert bsStyle="success">{this.state.successAlert}</Alert>}
        {canCreateReport ? (
          <div className="reportPanel">
            <DashboardReportCard
              title="Global Package Report Bundle"
              description="An organization-wide report on licenses and packages."
              emailDeliveryMessage="The report will be emailed to you."
            >
              <div>
                <Button type="secondary" size="large" onClick={this.generateReportBundle}>
                  {GenerateReportIconText}
                </Button>
              </div>
            </DashboardReportCard>

            {this.renderAuditReportGlobalTeamFilter()}

            <div>
              <DashboardReportCard
                title="Global Issue CSV Export"
                description="Organization-wide issue reports in CSV format. Contains separate reports for license, security, and quality issues."
              >
                <DropdownButton
                  id="GlobalCSVExportV2Options"
                  title={GenerateReportIconText}
                  size="large"
                  onSelect={this.generateCSVExport}
                  disabled={this.props.isIssueCSVExportLoading}
                >
                  <DropdownButton.Item eventKey="download">Download</DropdownButton.Item>
                  <DropdownButton.Item eventKey="email">Email</DropdownButton.Item>
                </DropdownButton>
              </DashboardReportCard>
            </div>

            {/* this deprecated report (available via FF) is not available for Team Report users,
            only OrgReport users as it does not respect the Team filters required for TeamReport usage */}
            {this.props.isSecurityAndPDFReportsEnabled && userHasPermissionToCreateOrgReports() &&
              this.renderBaseExport({
                title: 'Global Security Issue Export',
                description:
                  'An organization-wide issue export in PDF format. Contains a detailed breakdown of security issues.',
                onClick: this.generateSecurityExport,
                propsLoadingField: this.props.creatingSecurityReportLoading,
              })}

            <SubscriptionBannerReportPage />
          </div>
        ) : (
          <div className="reportPanel">
            <div className="panel reportInfo">
              <div>
                <Text.H1>Generating Reports</Text.H1>
                <hr />
                <p>You are not permitted to create organization-wide reports.</p>
              </div>
            </div>
          </div>
        )}

        <ModalBulkReport
          showBinaryScansOption={this.props.isBinaryDecompositionEnabled}
          show={this.state.showingModalBulkReport}
          onHide={() => this.setState({ showingModalBulkReport: false })}
        />
      </div>
    );
  }
}

/**
 * A sort-of higher-order component that wraps the DashboardActionCards component and
 * provides the issue CSV export AsyncValue as a prop. This used to be in the IssuesV1
 * Redux store, but was moved into a simple hook as part of deleting the IssuesV1 store.
 * Also provides the result of the useCanDisplaySecurityOptions hook as a prop.
 */
const DashboardActionCardsWithIssueCSVExport: FunctionComponent<
  Omit<
    Props,
    | 'onCreateIssueCSVExportClick'
    | 'isIssueCSVExportLoading'
    | 'isSecurityAndPDFReportsEnabled'
    | 'isBinaryDecompositionEnabled'
  >
> = (props) => {
  const isSecurityAndPDFReportsEnabled = useCanDisplaySecurityOptions();
  const isBinaryDecompositionEnabled = useIsFeatureEnabled(BINARY_DECOMPOSITION);
  const asyncValue = useAsyncValue(null);
  const emailGlobalIssueCSVExport = useCallback(async (teamIds?: TeamSelection) => {
    //  Note that we are intentionally not catching errors here. This is because the component does that already in the submitFn.
    asyncValue.setPending();
    const url = '/api/v2/issues/csv/global';
    const params = {
      email: true,
    };
    // if no teamIds are selected in the global filter we don't want to send the param
    if (!_.isEmpty(teamIds)) {
      Object.assign(params, { teamIds });
    }
    const response = await get<Task>(url, false, params);
    //  If there is actually a response with a jobToken, we can add it to the queue
    //  of tasks which are polled for completion.
    if (response.ok && response.data?.jobToken) {
      props.onAddJobToNotificationQueue(response.data.jobToken, response.data.task);
    }

    asyncValue.setResolve(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- The linter marks entire `props` as required here, which isn't correct
  }, [asyncValue, props.onAddJobToNotificationQueue]);

  return (
    <DashboardActionCards
      isIssueCSVExportLoading={asyncValue.isLoading}
      onCreateIssueCSVExportClick={emailGlobalIssueCSVExport}
      isSecurityAndPDFReportsEnabled={isSecurityAndPDFReportsEnabled}
      isBinaryDecompositionEnabled={isBinaryDecompositionEnabled}
      // eslint-disable-next-line react/jsx-props-no-spreading -- It's fine
      {...props}
    />
  );
};

/**
 * HOC to provide the addJobToNotificationQueue function to the DashboardActionCards component.
 */
const DashboardActionCardsWithAddJob: FunctionComponent<
  Omit<Props,
    | 'onCreateIssueCSVExportClick'
    | 'isIssueCSVExportLoading'
    | 'isSecurityAndPDFReportsEnabled'
    | 'onAddJobToNotificationQueue'>
> = (props) => {
  const addJob = useAddJobToNotificationQueue();
  return (
    <DashboardActionCardsWithIssueCSVExport
      onAddJobToNotificationQueue={addJob}
      // eslint-disable-next-line react/jsx-props-no-spreading -- It's fine
      {...props}
    />
  );
};

const mapStateToProps = createStructuredSelector({
  selectedTeams: TeamsModule.selectors.getTeamSelection,
  organizationsStatus: OrganizationsModule.selectors.getStatus,
  creatingAuditReportLoading: OrganizationsModule.selectors.getCreatingAuditReportLoading,
  creatingSecurityReportLoading: VulnsModule.selectors.getCreatingSecurityExportLoading,
});

const mapDispatchToProps = {
  createAuditReport: OrganizationsModule.actions.createAuditReport,
  createSecurityExport: VulnsModule.actions.createOrgSecurityReport,
};

export default connect(mapStateToProps, mapDispatchToProps)(DashboardActionCardsWithAddJob as any);
